import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import ServiceEventTracking from 'services/service.event-tracking';
import { getErrorMessage } from 'utils/errors';
import * as fetch from 'utils/fetch';
import {
  TalentEngineFormFields,
  TalentEngineFormFieldsSchema,
  UseTalentEngineForm
} from './TalentEngineFooter.types';

export const useTalentEngineFooter: UseTalentEngineForm = () => {
  const { t } = useI18n('talent-engine-landing-v3');

  const { addErrorToast, addSuccessToast } = useToasts();

  const DEFAULT_FORM_VALUES = useMemo(
    () => ({
      name: '',
      company: '',
      email: ''
    }),
    []
  );

  const formMethods = useForm<TalentEngineFormFields>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(TalentEngineFormFieldsSchema)
  });

  const isFormFilled =
    !!formMethods.watch('name') &&
    !!formMethods.watch('company') &&
    !!formMethods.watch('email');

  const onSubmit = useCallback(
    async (data: TalentEngineFormFields) => {
      try {
        await fetch.post<TalentEngineFormFields, void>('/api/teca', data);

        ServiceEventTracking.track({
          type: 'Landing - Form Submitted',
          properties: {
            objectName: 'talent-engine-demo',
            formContent: {
              name: data.name,
              company: data.company,
              email: data.email
            }
          }
        });

        addSuccessToast(
          t('talent-engine-landing.form.messages.submit_success')
        );
        formMethods.reset(DEFAULT_FORM_VALUES);
      } catch (ex) {
        addErrorToast(
          t('talent-engine-landing.form.messages.submit_success', {
            error: getErrorMessage(ex)
          }),
          t('talent-engine-landing.form.messages.submit_error')
        );
      }
    },
    [DEFAULT_FORM_VALUES, addErrorToast, addSuccessToast, formMethods, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.trackLandingFormFocused('talent-engine-demo');
  }, []);

  return {
    t,
    onSubmit,
    formMethods,
    isFormFilled,
    onFormFieldFocus
  };
};
